const BULA_FILE_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const BULA_FILE_IN_URL_PATTERN =
  /(?:^|\/)(?:bulas|attachments)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.pdf\/?(?:[?#]|$)/i;

export function isBulaFileId(id: string): boolean {
  return BULA_FILE_UUID_PATTERN.test(id);
}

export function extractBulaFileId(source: unknown): string | null {
  if (typeof source !== 'string') {
    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const record = source as Record<string, unknown>;
      return (
        extractBulaFileId(record.original) ||
        extractBulaFileId(record.thumbnail) ||
        extractBulaFileId(record.path)
      );
    }
    return null;
  }

  const fromUrl = source.match(BULA_FILE_IN_URL_PATTERN);
  if (fromUrl?.[1] && isBulaFileId(fromUrl[1])) {
    return fromUrl[1].toLowerCase();
  }

  const trimmed = source.trim();
  if (isBulaFileId(trimmed)) {
    return trimmed.toLowerCase();
  }

  return null;
}

/**
 * Public same-origin path for a leaflet stored in S3.
 * Returns null when no UUID can be mapped from the payload.
 */
export function toPublicBulaPath(source: unknown): string | null {
  const id = extractBulaFileId(source);
  return id ? `/bulas/${id}.pdf` : null;
}

const CONTROL_CHARS_PATTERN = /[\u0000-\u001F\u007F]/;

/** Only http(s) URLs or same-origin paths — never javascript:/data: hrefs. */
function asSafeBulaHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || CONTROL_CHARS_PATTERN.test(trimmed)) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return trimmed;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.includes('\\')) {
    return trimmed;
  }

  return null;
}

/** Href safe to pass to `<a>`: own-domain path when possible, else a string URL. */
export function resolveBulaPdfUrl(source: unknown): string | null {
  const publicPath = toPublicBulaPath(source);
  if (publicPath) {
    return publicPath;
  }

  if (typeof source === 'string') {
    return asSafeBulaHref(source);
  }

  if (source && typeof source === 'object' && !Array.isArray(source)) {
    const record = source as Record<string, unknown>;
    return (
      resolveBulaPdfUrl(record.original) ||
      resolveBulaPdfUrl(record.thumbnail) ||
      resolveBulaPdfUrl(record.path)
    );
  }

  return null;
}
